import { jsx as _jsx } from "react/jsx-runtime";
import { ClipType } from "@air/api/types";
import { VIDEO_SPRITESHEET_COLUMN_COUNT, VIDEO_SPRITESHEET_FRAME_COUNT, VIDEO_SPRITESHEET_ROW_COUNT } from "@air/component-video";
import { Spinner } from "@air/primitive-spinner";
import { tailwindMerge } from "@air/tailwind-variants";
import { getClipAspectRatio } from "@air/utils-clips";
import { memo, useEffect, useMemo, useState } from "react";
export const CHAPTER_CARD_THUMBNAIL = "CHAPTER_CARD_THUMBNAIL";
export const SpriteSheetThumbnail = /*#__PURE__*/ memo((param)=>{
    let { clip, startTime, endTime, width = 80, height = 48, className } = param;
    var _clip_assets, _clip_assets1;
    const [isLoading, setIsLoading] = useState(true);
    const timePosition = useMemo(()=>{
        if (endTime && endTime > startTime) {
            return startTime + (endTime - startTime) / 2;
        }
        return startTime;
    }, [
        startTime,
        endTime
    ]);
    // Scale dimensions based on aspect ratio, similar to VideoSeekbarPreviewSpritesheet
    const { scaledDimensions, backgroundPosition, backgroundSize } = useMemo(()=>{
        var _clip_assets;
        if (!(clip === null || clip === void 0 ? void 0 : (_clip_assets = clip.assets) === null || _clip_assets === void 0 ? void 0 : _clip_assets.spriteSheet) || !clip.duration) {
            return {
                scaledDimensions: {
                    width,
                    height
                },
                backgroundPosition: "0 0",
                backgroundSize: "cover"
            };
        }
        const aspectRatio = getClipAspectRatio(clip);
        const isVertical = aspectRatio < 1;
        const maxDimension = Math.max(width, height);
        let scaledWidth, scaledHeight;
        if (isVertical) {
            // For vertical videos, fix height to maxDimension and scale width proportionally
            scaledHeight = maxDimension;
            scaledWidth = Math.round(maxDimension * aspectRatio);
        } else {
            // For horizontal videos, fix width to maxDimension and scale height proportionally
            scaledWidth = maxDimension;
            scaledHeight = Math.round(maxDimension / aspectRatio);
        }
        // Calculate frame position
        const position = timePosition / clip.duration;
        const frameIndex = Math.floor(position * VIDEO_SPRITESHEET_FRAME_COUNT);
        const safeFrameIndex = Math.min(VIDEO_SPRITESHEET_FRAME_COUNT - 1, Math.max(0, frameIndex));
        const row = Math.floor(safeFrameIndex / VIDEO_SPRITESHEET_COLUMN_COUNT);
        const column = safeFrameIndex % VIDEO_SPRITESHEET_COLUMN_COUNT;
        // Calculate background position and size
        const bgPositionX = -column * scaledWidth;
        const bgPositionY = -row * scaledHeight;
        const bgWidth = scaledWidth * VIDEO_SPRITESHEET_COLUMN_COUNT;
        const bgHeight = scaledHeight * VIDEO_SPRITESHEET_ROW_COUNT;
        return {
            scaledDimensions: {
                width: scaledWidth,
                height: scaledHeight
            },
            backgroundPosition: "".concat(bgPositionX, "px ").concat(bgPositionY, "px"),
            backgroundSize: "".concat(bgWidth, "px ").concat(bgHeight, "px")
        };
    }, [
        clip,
        timePosition,
        width,
        height
    ]);
    // Simple loading effect
    useEffect(()=>{
        var _clip_assets;
        if (!(clip === null || clip === void 0 ? void 0 : (_clip_assets = clip.assets) === null || _clip_assets === void 0 ? void 0 : _clip_assets.spriteSheet)) {
            setIsLoading(false);
            return;
        }
        let isMounted = true;
        setIsLoading(true);
        const img = new Image();
        img.onload = ()=>{
            if (isMounted) setIsLoading(false);
        };
        img.onerror = ()=>{
            if (isMounted) setIsLoading(false);
        };
        img.src = clip.assets.spriteSheet;
        return ()=>{
            isMounted = false;
        };
    }, [
        clip === null || clip === void 0 ? void 0 : (_clip_assets = clip.assets) === null || _clip_assets === void 0 ? void 0 : _clip_assets.spriteSheet
    ]);
    if (!(clip === null || clip === void 0 ? void 0 : (_clip_assets1 = clip.assets) === null || _clip_assets1 === void 0 ? void 0 : _clip_assets1.spriteSheet) || clip.type !== ClipType.video) {
        return null;
    }
    return /*#__PURE__*/ _jsx("div", {
        className: tailwindMerge("overflow-hidden rounded-4 bg-grey-3 bg-no-repeat", isLoading ? "opacity-50" : "", className),
        style: {
            ...scaledDimensions,
            backgroundImage: "url(".concat(clip.assets.spriteSheet, ")"),
            backgroundSize,
            backgroundPosition
        },
        "data-testid": CHAPTER_CARD_THUMBNAIL,
        children: isLoading && /*#__PURE__*/ _jsx("div", {
            className: "flex size-full items-center justify-center",
            children: /*#__PURE__*/ _jsx(Spinner, {
                className: "size-4"
            })
        })
    });
});
SpriteSheetThumbnail.displayName = "SpriteSheetThumbnail";
